/**
 * "Reserved" is not its own field — it IS `statusSold === 'Yes'` (#1221).
 *
 * One flag carries both meanings across the whole stack: the Google Sheet
 * column `status_sold`, the Meilisearch document's `statusSold`, and the
 * catalog's grey `RESERVED` ribbon (`CarCard.jsx`). Every storefront read of
 * that flag goes through here so the rule can't drift between surfaces — the
 * catalog's "Available only" filter, the search suggestions exclusion and the
 * results-page ranking must agree on what "reserved" means.
 *
 * Case-insensitive, and deliberately NOT `!== 'No'`: an empty / missing value
 * is a first-class "available" (the b2b-ui sold toggle writes `'Yes'` or an
 * empty string), which is the same asymmetry the backend encodes as
 * `statusSold != 'Yes'` rather than `= 'No'`.
 *
 * @param {{ statusSold?: string }} [car]
 * @returns {boolean}
 */
export function isReservedCar(car) {
  return car?.statusSold?.toLowerCase() === 'yes';
}

/**
 * Drop reserved cars from a list of search hits.
 *
 * Used by the suggestions dropdowns, which have no status badge — an unlabelled
 * reserved car there reads as available. The results page keeps them (badged by
 * `CarCard`, ranked last), so the two surfaces differ on purpose.
 *
 * @template {{ statusSold?: string }} T
 * @param {T[]} [hits]
 * @returns {T[]}
 */
export function excludeReservedCars(hits) {
  return (hits ?? []).filter((car) => !isReservedCar(car));
}

/**
 * Comparator fragment that sorts reserved cars AFTER available ones (#1221).
 *
 * `0` when both sides agree, which makes it composable: the caller falls through
 * to its real ordering key (Meilisearch rank on the results page) whenever the
 * reserved flags match.
 *
 * NOTE this is an ABSOLUTE preference — every reserved car sorts after every
 * available one, however irrelevant. On the results page it is applied WITHIN a
 * relevance band only (see `compareSearchRelevance`), never across bands.
 *
 * @param {{ statusSold?: string }} a
 * @param {{ statusSold?: string }} b
 * @returns {number}
 */
export function compareReservedLast(a, b) {
  return (isReservedCar(a) ? 1 : 0) - (isReservedCar(b) ? 1 : 0);
}

/** Rank value for a car the current search didn't match — always sorts last. */
export const UNRANKED = Number.MAX_SAFE_INTEGER;

/**
 * Width of a relevance band (#1234, revised after measuring on test).
 *
 * Meilisearch `_rankingScore` is a 0–1 relevance figure. Cars whose scores fall
 * in the same band are treated as equally relevant, and availability decides the
 * order within it. 0.05 is wide enough that near-identical matches group
 * together — measured on the Andys test index, `q=yaris` returns eight cars
 * scoring 0.999–1.000, i.e. one band — and narrow enough to keep genuinely weak
 * matches apart: the "bmv" alias collision scored 0.02, and a description-only
 * hit scored 0.54.
 */
export const RELEVANCE_BAND_SIZE = 0.05;

/**
 * Bucket a `_rankingScore` into a comparable band. Higher band = more relevant.
 *
 * A missing score (older API response, or `available: false`) collapses to band 0,
 * so every car shares one band and availability alone decides — the only sane
 * fallback when there is no relevance signal at all.
 *
 * KNOWN LIMITATION: bucketing has cliffs. Two scores a hair apart but straddling
 * a boundary (0.9749 → band 19, 0.9751 → band 20) land in different bands, so a
 * reserved car can edge ahead of an available one that is fractionally less
 * relevant — the #1234 symptom again, in a narrow window.
 *
 * This is accepted rather than solved, because the obvious alternative is worse:
 * "equal if |a − b| < ε" is NOT transitive (a≈b, b≈c, a≉c), which makes the
 * comparator inconsistent and `Array.prototype.sort` output formally arbitrary.
 * Bucketing is the standard fix precisely because it IS transitive. If the cliff
 * ever bites in practice, widen the band or normalise scores against the top hit
 * — both keep transitivity; an epsilon does not.
 */
export function relevanceBand(score) {
  if (typeof score !== 'number' || Number.isNaN(score)) return 0;
  return Math.round(score / RELEVANCE_BAND_SIZE);
}

/**
 * Comparator for the search results page: relevance BAND first, then buyable
 * cars, then exact relevance (#1234).
 *
 * Replaces the fixed "+5 positions" penalty this issue originally shipped. That
 * heuristic assumed rank positions carry relevance information, and measuring on
 * test showed they often don't: for `q=yaris` all eight hits scored 0.999–1.000,
 * so the two reserved cars simply held ranks 0 and 1, and a 5-place shift dropped
 * them into the MIDDLE of the available ones — visible to the dealer as reserved
 * cars interleaved with available ones. Bands fix that: identical relevance ⇒ one
 * band ⇒ every available car first.
 *
 * It also preserves what the penalty got right. A reserved car that is genuinely
 * far more relevant than the alternatives stays high, because it lands in a higher
 * band — the case that motivated dropping the absolute rule (#1221) in the first
 * place, where a reserved BMW sat behind unrelated available Mazdas.
 *
 * @param {{ statusSold?: string, id?: string }} a
 * @param {{ statusSold?: string, id?: string }} b
 * @param {{ score: (car: object) => number|undefined, rank: (car: object) => number|undefined }} lookup
 */
export function compareSearchRelevance(a, b, lookup) {
  const bandDelta = relevanceBand(lookup.score(b)) - relevanceBand(lookup.score(a));
  if (bandDelta !== 0) return bandDelta; // higher score first

  const reservedDelta = compareReservedLast(a, b);
  if (reservedDelta !== 0) return reservedDelta;

  // Same band, same availability — fall back to Meilisearch's own ordering.
  return (lookup.rank(a) ?? UNRANKED) - (lookup.rank(b) ?? UNRANKED);
}

/**
 * The suggestions-dropdown contract, shared by both search surfaces
 * (`registry/Search` embed and the in-catalog `SearchBar`) so they cannot drift.
 *
 * Two counts, deliberately different (#1221):
 *
 *   - `rows` — what the dropdown renders: available cars only, capped at
 *     `limit`. Filtered BEFORE the cap, so a query whose top hits are reserved
 *     still fills all `limit` rows out of the ~100 requested.
 *   - `totalHits` — the whole result set, reserved included. It labels the
 *     RESULTS page the buyer is about to open, and that page does show them.
 *
 * Hence `showAll` compares the total against what was rendered rather than
 * against the cap: when the surplus matches are reserved they are missing from
 * `rows` but present on the results page, so the buyer still needs the link.
 * And `isEmpty` tracks a genuinely empty response — an all-reserved page of
 * hits is not "no cars found", it's "none of them shown here".
 *
 * @param {{ hits?: Array<{ statusSold?: string }>, estimatedTotalHits?: number }} [results]
 * @param {number} limit  max rows to render
 * @returns {{ rows: Array<object>, totalHits: number, showAll: boolean, isEmpty: boolean }}
 */
export function deriveSuggestions(results, limit) {
  const hits = results?.hits ?? [];
  const rows = excludeReservedCars(hits).slice(0, limit);
  const totalHits = results?.estimatedTotalHits ?? hits.length;
  return {
    rows,
    totalHits,
    showAll: totalHits > rows.length,
    isEmpty: hits.length === 0,
  };
}
